import React, { useState } from 'react';

const moods = [
  { score: 1, emoji: '😔', label: 'Very low', color: '#F08080' },
  { score: 2, emoji: '😟', label: 'Low', color: '#F4A261' },
  { score: 3, emoji: '😐', label: 'Meh', color: '#F7C948' },
  { score: 4, emoji: '🙂', label: 'Okay', color: '#A8D8A8' },
  { score: 5, emoji: '😊', label: 'Good', color: '#7BC67E' },
  { score: 6, emoji: '😄', label: 'Great', color: '#63C5DA' },
  { score: 7, emoji: '🤩', label: 'Amazing', color: '#C3A8E1' },
];

const emotions = ['😌 Calm', '😰 Anxious', '😤 Frustrated', '🥰 Loved', '😴 Tired', '💪 Energized', '🥺 Sad', '🎉 Excited', '🤔 Confused', '😎 Confident', '🙏 Grateful', '😓 Stressed'];

const styles = {
  page: { minHeight: '100vh', background: '#FDF8F5', display: 'flex', flexDirection: 'column' },
  header: { display: 'flex', alignItems: 'center', gap: '16px', padding: '20px 32px', background: '#FFFFFF', borderBottom: '1px solid #EDE8E3' },
  backBtn: { background: '#F5F0EB', border: 'none', borderRadius: '999px', padding: '10px 20px', fontSize: '14px', fontWeight: 500, color: '#6B6B6B', cursor: 'pointer' },
  title: { fontSize: '18px', fontWeight: 600 },
  content: { flex: 1, maxWidth: '600px', width: '100%', margin: '0 auto', padding: '48px 24px' },
  question: { fontSize: '28px', fontFamily: "'Playfair Display', serif", fontWeight: 600, textAlign: 'center', marginBottom: '40px', color: '#2D2D2D' },
  moodGrid: { display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '40px' },
  moodBtn: (selected, color) => ({ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', padding: '20px 24px', borderRadius: '20px', border: `2px solid ${selected ? color : '#EDE8E3'}`, background: selected ? `${color}20` : '#FFFFFF', cursor: 'pointer', transition: 'all 0.2s', minWidth: '80px' }),
  moodEmoji: { fontSize: '36px' },
  moodLabel: (selected, color) => ({ fontSize: '12px', fontWeight: selected ? 600 : 400, color: selected ? color : '#A0A0A0' }),
  section: { marginBottom: '32px' },
  sectionTitle: { fontSize: '14px', fontWeight: 600, color: '#6B6B6B', marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '0.06em' },
  emotionGrid: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  emotionBtn: (selected) => ({ padding: '10px 16px', borderRadius: '999px', border: `1px solid ${selected ? '#C3A8E1' : '#EDE8E3'}`, background: selected ? '#EDE8F7' : '#FFFFFF', color: selected ? '#9B7EC8' : '#6B6B6B', fontSize: '14px', cursor: 'pointer', transition: 'all 0.15s', fontWeight: selected ? 500 : 400 }),
  notesInput: { width: '100%', padding: '16px 20px', borderRadius: '16px', border: '1px solid #EDE8E3', background: '#FFFFFF', fontSize: '15px', color: '#2D2D2D', outline: 'none', resize: 'none', lineHeight: 1.7, fontFamily: 'Inter, sans-serif' },
  saveBtn: (ready) => ({ width: '100%', padding: '18px', borderRadius: '999px', background: ready ? 'linear-gradient(135deg, #F7C948, #F4A261)' : '#EDE8E3', border: 'none', fontWeight: 700, fontSize: '17px', color: ready ? '#2D2D2D' : '#A0A0A0', cursor: ready ? 'pointer' : 'default', boxShadow: ready ? '0 8px 24px rgba(247,201,72,0.35)' : 'none', transition: 'all 0.2s' }),
};

export default function MoodLog({ navigate, userId }) {
  const [selected, setSelected] = useState(null);
  const [selectedEmotions, setSelectedEmotions] = useState([]);
  const [notes, setNotes] = useState('');
  const [saved, setSaved] = useState(false);

  const toggleEmotion = (e) => setSelectedEmotions(prev => prev.includes(e) ? prev.filter(x => x !== e) : [...prev, e]);

  const handleSave = async () => {
    if (!selected) return;
    try {
      await fetch('/api/moods', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, score: selected.score, emotions: selectedEmotions, notes }),
      });
      setSaved(true);
      setTimeout(() => navigate('dashboard'), 1500);
    } catch (e) { console.error(e); }
  };

  if (saved) return (
    <div style={{ minHeight: '100vh', background: '#FDF8F5', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '16px' }}>
      <div style={{ fontSize: '64px' }}>{selected?.emoji}</div>
      <div style={{ fontSize: '24px', fontFamily: "'Playfair Display', serif", fontWeight: 600 }}>Mood logged</div>
      <div style={{ color: '#A0A0A0' }}>Taking you back...</div>
    </div>
  );

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <button style={styles.backBtn} onClick={() => navigate('dashboard')}>← Back</button>
        <div style={styles.title}>Log Mood</div>
      </div>
      <div style={styles.content}>
        <div style={styles.question}>How are you feeling right now?</div>
        <div style={styles.moodGrid}>
          {moods.map((m) => (
            <button key={m.score} style={styles.moodBtn(selected?.score === m.score, m.color)} onClick={() => setSelected(m)}>
              <span style={styles.moodEmoji}>{m.emoji}</span>
              <span style={styles.moodLabel(selected?.score === m.score, m.color)}>{m.label}</span>
            </button>
          ))}
        </div>
        {selected && (
          <>
            <div style={styles.section}>
              <div style={styles.sectionTitle}>What emotions are present?</div>
              <div style={styles.emotionGrid}>
                {emotions.map(e => (
                  <button key={e} style={styles.emotionBtn(selectedEmotions.includes(e))} onClick={() => toggleEmotion(e)}>{e}</button>
                ))}
              </div>
            </div>
            <div style={styles.section}>
              <div style={styles.sectionTitle}>Any notes? (optional)</div>
              <textarea style={styles.notesInput} placeholder="What's contributing to this feeling?" value={notes} onChange={e => setNotes(e.target.value)} rows={3} />
            </div>
            <button style={styles.saveBtn(true)} onClick={handleSave}>Save mood ✨</button>
          </>
        )}
      </div>
    </div>
  );
}
